import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, Building2, Store, Copy, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmt = (n) => "Rp" + Number(n).toLocaleString("id-ID");

export default function LocalCheckout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const d = loc.state;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!d) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4 text-center">
        <div><p className="font-heading font-bold text-xl mb-3">Sesi pembayaran tidak ditemukan</p><Link to="/pricing"><Btn>Kembali ke Harga</Btn></Link></div>
      </div>
    );
  }

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Disalin!"); };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/payments/local/confirm", { session_id: d.session_id });
      await refresh();
      setDone(true);
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4">
        <div className="card-chunky p-8 max-w-md w-full text-center animate-pop">
          <div className="w-20 h-20 rounded-2xl bg-su-success/15 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-11 h-11 text-su-success" /></div>
          <h1 className="font-heading font-bold text-2xl mb-1" data-testid="local-paid">Pembayaran Berhasil! 🎉</h1>
          <p className="text-su-muted mb-6">Manfaat sudah aktif di akunmu.</p>
          <Link to="/dashboard"><Btn className="w-full">Ke Dashboard</Btn></Link>
        </div>
      </div>
    );
  }

  const isQr = ["qris", "gopay", "dana", "shopeepay"].includes(d.method);

  return (
    <div className="max-w-md mx-auto py-6" data-testid="local-checkout">
      <div className="card-chunky p-6">
        <div className="text-center mb-5">
          <p className="text-xs font-heading font-bold uppercase text-su-muted">{d.method_label}</p>
          <h1 className="font-heading font-bold text-2xl">{d.product_name}</h1>
          <p className="font-heading font-bold text-3xl text-su-primary mt-1">{fmt(d.amount)}</p>
        </div>

        {isQr && (
          <div className="flex flex-col items-center">
            <div className="w-56 h-56 rounded-2xl bg-white p-3 grid place-items-center mb-3">
              <QRVisual seed={d.qr_string} />
            </div>
            <p className="text-sm text-su-muted text-center">Buka aplikasi <b>{d.method_label}</b> / m-banking, pilih <b>Scan QRIS</b>, lalu bayar sesuai nominal.</p>
          </div>
        )}

        {d.method === "va" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-su-muted"><Building2 className="w-5 h-5" /> Nomor Virtual Account</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono font-bold text-xl bg-su-bg border border-su-border rounded-xl px-4 py-3 tracking-wider" data-testid="va-number">{d.va_number}</div>
              <Btn variant="ghost" onClick={() => copy(d.va_number)}><Copy className="w-5 h-5" /></Btn>
            </div>
            <p className="text-sm text-su-muted">Transfer ke nomor VA di atas melalui m-banking/ATM (BCA, BNI, Mandiri, BRI).</p>
          </div>
        )}

        {d.method === "retail" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-su-muted"><Store className="w-5 h-5" /> Kode Pembayaran</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono font-bold text-xl bg-su-bg border border-su-border rounded-xl px-4 py-3 tracking-wider" data-testid="retail-code">{d.retail_code}</div>
              <Btn variant="ghost" onClick={() => copy(d.retail_code)}><Copy className="w-5 h-5" /></Btn>
            </div>
            <p className="text-sm text-su-muted">Tunjukkan kode ini ke kasir <b>Alfamart / Indomaret</b> dan bayar tunai.</p>
          </div>
        )}

        <div className="mt-5 rounded-xl bg-su-energy/10 border border-su-energy/30 p-3 text-xs text-su-muted flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-su-energy shrink-0 mt-0.5" /> Mode simulasi demo — tekan tombol di bawah untuk mensimulasikan pembayaran berhasil. Integrasi gateway asli (Midtrans/Xendit) dapat ditambahkan nanti.
        </div>

        <Btn data-testid="local-confirm" onClick={confirm} disabled={busy} className="w-full mt-4">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Memproses...</> : "Saya Sudah Bayar (Simulasi)"}
        </Btn>
        <Link to="/pricing" className="block text-center text-sm text-su-muted mt-3 font-bold">Batalkan</Link>
      </div>
    </div>
  );
}

function QRVisual({ seed }) {
  // deterministic pseudo-QR grid from seed string with finder patterns
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const n = 25;
  const grid = Array.from({ length: n }, () => Array(n).fill(0));
  let x = h || 1;
  const rnd = () => { x = (x * 1103515245 + 12345) >>> 0; return (x >>> 8) & 1; };
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) grid[r][c] = rnd();
  // stamp a 7x7 finder pattern at (or,oc)
  const finder = (or, oc) => {
    for (let r = 0; r < 7; r++) for (let c = 0; c < 7; c++) {
      const edge = r === 0 || r === 6 || c === 0 || c === 6;
      const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      grid[or + r][oc + c] = edge || core ? 1 : 0;
    }
    // quiet ring
    for (let k = -1; k <= 7; k++) {
      if (or + k >= 0 && or + k < n && oc + 7 < n) grid[or + k][oc + 7] = 0;
      if (oc + k >= 0 && oc + k < n && or + 7 < n) grid[or + 7][oc + k] = 0;
    }
  };
  finder(0, 0); finder(0, n - 7); finder(n - 7, 0);
  const rects = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (grid[r][c]) rects.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#0a0a0a" />);
  return (
    <svg viewBox={`-1 -1 ${n + 2} ${n + 2}`} shapeRendering="crispEdges" className="w-full h-full">
      {rects}
    </svg>
  );
}
